import axios, { type AxiosInstance } from "axios";

export interface ApiResponse<TModelApi> {
  body: TModelApi;
}

interface ResponseParams {
  raw?: boolean;
}

export class ApiService {
  public client: AxiosInstance;

  constructor(baseUrl: string) {
    this.client = axios.create({
      baseURL: baseUrl,
    });
  }

  public async get<TParams, TModel>({
    endpoint,
    params,
    headers,
    responseParams = { raw: false },
  }: {
    endpoint: string;
    params?: TParams;
    headers?: Record<string, string>;
    responseParams?: ResponseParams;
    options?: {
      deviceToken?: boolean;
    };
  }): Promise<TModel> {
    if (!responseParams.raw) {
      const response = await this.client.get<ApiResponse<TModel>>(endpoint, {
        params,
        headers,
      });

      return response.data.body;
    } else {
      const response = await this.client.get<TModel>(endpoint, {
        params,
        headers,
      });

      return response.data;
    }
  }

  public async post<TBody, TModel>({
    endpoint,
    headers,
    body = null,
    responseParams = { raw: false },
  }: {
    endpoint: string;
    headers?: Record<string, string>;
    body?: TBody | null;
    responseParams?: ResponseParams;
  }): Promise<TModel> {
    if (!responseParams.raw) {
      const response = await this.client.post<ApiResponse<TModel>>(endpoint, body, {
        headers,
      });

      return response.data.body;
    } else {
      const response = await this.client.post<TModel>(endpoint, body, {
        headers,
      });

      return response.data;
    }
  }

  public async patch<TBody, TModel>({
    endpoint,
    headers,
    body = null,
    responseParams = { raw: false },
  }: {
    endpoint: string;
    headers?: Record<string, string>;
    body?: TBody | null;
    responseParams?: ResponseParams;
  }): Promise<TModel> {
    if (!responseParams.raw) {
      const response = await this.client.patch<ApiResponse<TModel>>(endpoint, body, {
        headers,
      });

      return response.data.body;
    } else {
      const response = await this.client.patch<TModel>(endpoint, body, {
        headers,
      });

      return response.data;
    }
  }

  public async delete<TParams, TModel>({
    endpoint,
    headers,
    data,
    responseParams = { raw: false },
  }: {
    endpoint: string;
    headers?: Record<string, string>;
    data?: TParams;
    responseParams?: ResponseParams;
  }): Promise<TModel> {
    if (!responseParams.raw) {
      const response = await this.client.delete<ApiResponse<TModel>>(endpoint, {
        headers,
        data,
      });

      return response.data.body;
    } else {
      const response = await this.client.delete<TModel>(endpoint, {
        headers,
        data,
      });

      return response.data;
    }
  }
}
